/**
 * Broadcast Message Modal Component
 * Shows broadcast messages with markdown support and user acknowledgment
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Button, Checkbox, Typography } from 'antd';
import { InfoCircleOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { marked } from 'marked';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { closeBroadcastModal, nextBroadcastMessage, updateBroadcastLog } from '../../../store/dashboardSlice';
import './BroadcastMessageModal.css';

const { Text } = Typography;

// Configure marked options (similar to AngularJS showdown configuration)
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

function BroadcastMessageModal() {
  const dispatch = useAppDispatch();
  const { info, activeModal, currentBroadcastIndex } = useAppSelector(state => state.dashboard);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const isMountedRef = useRef(true);

  // Get current broadcast messages that user hasn't seen
  const unreadMessages = info?.broadcast_messages?.filter(msg => !msg.has_user_seen) || [];
  const currentMessage = unreadMessages[currentBroadcastIndex];
  const isVisible = activeModal === 'broadcast' && !!currentMessage;
  const hasMoreMessages = currentBroadcastIndex < unreadMessages.length - 1;

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset checkbox and updating state when modal opens/closes or message changes
  useEffect(() => {
    setDoNotShowAgain(false);
    setIsUpdating(false);
  }, [isVisible, currentBroadcastIndex]);

  const handleOk = () => {
    if (!currentMessage) return;

    // Close or navigate first
    const shouldNavigate = hasMoreMessages;
    const shouldClose = !hasMoreMessages;

    if (shouldNavigate) {
      dispatch(nextBroadcastMessage());
    } else if (shouldClose) {
      dispatch(closeBroadcastModal());
    }

    // Mark message as seen if "do not show again" is checked
    // Do this AFTER closing/navigating to avoid state updates during unmount
    if (doNotShowAgain) {
      // Use setTimeout to ensure this happens after the modal has closed
      setTimeout(() => {
        dispatch(updateBroadcastLog({
          messageId: currentMessage.id,
          hasUserSeen: true,
          shouldCloseOrNavigate: false
        })).catch(error => {
          console.error('Failed to update broadcast log:', error);
        });
      }, 0);
    }
  };

  const handlePrevious = () => {
    if (currentBroadcastIndex > 0) {
      dispatch(nextBroadcastMessage()); // This will go to previous due to reducer logic
    }
  };

  const handleNext = () => {
    if (hasMoreMessages) {
      dispatch(nextBroadcastMessage());
    }
  };

  if (!currentMessage) {
    return null;
  }

  // Format dates for display
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';

    try {
      // Parse the date string (handles both ISO and YYYY-MM-DD formats)
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format as MM/DD/YYYY
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid Date';
    }
  };

  // Parse markdown to HTML (memoized to avoid re-parsing on every render)
  const messageHtml = useMemo(() => {
    if (!currentMessage?.body) return '';
    try {
      // Parse markdown and add target="_blank" to links
      let html = marked.parse(currentMessage.body) as string;
      // Add target="_blank" to all links for security
      html = html.replace(/<a href/g, '<a target="_blank" rel="noopener noreferrer" href');
      return html;
    } catch (error) {
      console.error('Error parsing markdown:', error);
      return currentMessage.body; // Fallback to plain text
    }
  }, [currentMessage?.body]);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
          <span>{currentMessage.title}</span>
          {unreadMessages.length > 1 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ({currentBroadcastIndex + 1} of {unreadMessages.length})
            </Text>
          )}
        </div>
      }
      open={isVisible}
      onOk={handleOk}
      onCancel={() => dispatch(closeBroadcastModal())}
      width={600}
      maskClosable={false}
      footer={[
        <div key="footer-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {unreadMessages.length > 1 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  onClick={handlePrevious}
                  disabled={currentBroadcastIndex === 0 || isUpdating}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  onClick={handleNext}
                  disabled={!hasMoreMessages || isUpdating}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button onClick={() => dispatch(closeBroadcastModal())} disabled={isUpdating}>
              Skip
            </Button>
            <Button type="primary" onClick={handleOk} loading={isUpdating}>
              {hasMoreMessages ? 'Next' : 'OK'}
            </Button>
          </div>
        </div>
      ]}
    >
      <div style={{ padding: '10px 0' }}>
        {/* Message dates */}
        <div style={{ marginBottom: '16px', fontSize: '12px', color: '#666' }}>
          <div>
            <strong>Effective:</strong> {formatDate(currentMessage.effective_date)}
          </div>
          <div>
            <strong>Expires:</strong> {formatDate(currentMessage.expiration_date)}
          </div>
        </div>

        {/* Message content - Markdown rendered as HTML */}
        <div
          style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #e9ecef',
            minHeight: '120px',
            lineHeight: '1.6'
          }}
        >
          {/* Render markdown as HTML */}
          <div
            dangerouslySetInnerHTML={{ __html: messageHtml }}
            style={{
              // Markdown styling
              wordWrap: 'break-word'
            }}
            className="markdown-content"
          />
        </div>

        {/* Do not show again checkbox */}
        <div style={{ marginTop: '16px' }}>
          <Checkbox 
            checked={doNotShowAgain} 
            onChange={(e) => setDoNotShowAgain(e.target.checked)}
          >
            <Text type="secondary" style={{ fontSize: '13px' }}>
              Do not show this message again
            </Text>
          </Checkbox>
        </div>
      </div>
    </Modal>
  );
}

export default BroadcastMessageModal;